import { useEffect, useRef, useSyncExternalStore } from 'react';
import { getReactiveFnAndDefinition, reactiveSignal } from '../internals/core-api.js';
import { getCurrentConsumer } from '../internals/consumer.js';
import { snapshot } from '../internals/utils/snapshot.js';
import { useScope } from './context.js';
import { usePauseSignalsManager } from './pause-signals-context.js';
import { getGlobalScope } from '../internals/contexts.js';
function useSignalWithSuspension(signal) {
    const manager = usePauseSignalsManager();
    const watch = !manager?.paused;
    manager?.register(signal);
    useEffect(() => {
        return () => manager?.unregister(signal);
    }, [manager, signal]);
    return useSyncExternalStore(signal.addListenerLazy(watch), () => signal.value, () => signal.value);
}
/**
 * Subscribe to a reactive thunk without structural cloning. The thunk's
 * `ReactiveDefinition` is memoized by fn identity in a `WeakMap`, so a
 * memoized thunk (via `useCallback` or the Signalium Babel preset) reuses the
 * same scope-cached signal across renders.
 *
 * This is a minimal wrapper: the returned value is whatever the thunk
 * returned, by reference. In particular, when the thunk returns a
 * `ReactivePromise`, re-renders only fire when the underlying signal itself
 * re-evaluates (e.g. a new promise replaces the old one) — not when the
 * existing promise transitions from pending to resolved. If you need promise
 * state transitions to drive React, read its fields inside the thunk (e.g.
 * `useReactiveShallow(() => { const p = fetchThing(); return { value: p.value,
 * isPending: p.isPending }; })`) or use {@link useReactive} for the
 * structurally-shared snapshot that handles this automatically.
 */
export function useReactiveShallow(fn) {
    if (IS_DEV && getCurrentConsumer()) {
        throw new Error('signalium: `useReactiveShallow` cannot be called inside a reactive function. ' +
            'Call your reactive function directly instead — it already participates in the signal graph.');
    }
    const [, def] = getReactiveFnAndDefinition(fn);
    const scope = useScope() ?? getGlobalScope();
    const signal = scope.get(def, []);
    return useSignalWithSuspension(signal);
}
/**
 * Subscribe to a reactive thunk and return a structurally-shared snapshot of
 * its value. Nested objects/arrays/Maps/Sets are deep-cloned; unchanged
 * subtrees keep the same reference, so React's referential equality works as
 * expected. ReactivePromise values are flattened to plain objects.
 *
 * This is the default hook for reading reactive values inside a React
 * component — it gives you safe equality semantics at the React boundary.
 * Use {@link useReactiveShallow} if you know you don't need structural
 * sharing.
 */
export function useReactive(fn) {
    if (IS_DEV && getCurrentConsumer()) {
        throw new Error('signalium: `useReactive` cannot be called inside a reactive function. ' +
            'Call your reactive function directly instead — it already participates in the signal graph.');
    }
    const manager = usePauseSignalsManager();
    const watch = !manager?.paused;
    const scope = useScope() ?? getGlobalScope();
    const innerSignalRef = useRef(undefined);
    const cloneSignalRef = useRef(undefined);
    const valueRef = useRef(undefined);
    const [, def] = getReactiveFnAndDefinition(fn);
    const signal = scope.get(def, []);
    if (innerSignalRef.current !== signal) {
        innerSignalRef.current = signal;
        valueRef.current = undefined;
        cloneSignalRef.current = reactiveSignal(() => {
            const next = snapshot(signal.value, valueRef.current);
            valueRef.current = next;
            return next;
        });
    }
    const cloneSignal = cloneSignalRef.current;
    manager?.register(cloneSignal);
    useEffect(() => {
        return () => manager?.unregister(cloneSignal);
    }, [manager, cloneSignal]);
    return useSyncExternalStore(cloneSignal.addListenerLazy(watch), () => cloneSignal.value, () => cloneSignal.value);
}
/**
 * @deprecated Use {@link useReactive} instead. `useReactive` is now
 * deep-by-default; `useReactiveDeep` is a thin alias kept for back-compat and
 * will be removed in a future major release.
 */
export function useReactiveDeep(fn) {
    if (IS_DEV) {
        warnUseReactiveDeepOnce();
    }
    return useReactive(fn);
}
let _useReactiveDeepWarned = false;
function warnUseReactiveDeepOnce() {
    if (_useReactiveDeepWarned)
        return;
    _useReactiveDeepWarned = true;
    console.warn('[signalium] `useReactiveDeep` is deprecated; use `useReactive` instead. ' +
        '`useReactive` is now deep-by-default. Use `useReactiveShallow` to opt out of structural snapshots.');
}
